import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { RouterModule, provideRouter } from '@angular/router';

import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { CurrencyPipe } from '@angular/common';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { ToastrModule } from 'ngx-toastr';
import { routes } from './app.routes';

import { AuthInterceptor } from './web/core/interceptors/auth.interceptor';
import { GlobalErrorHandlerInterceptor } from './web/core/interceptors/global-error-handler.interceptor';
import { LoaderInterceptor } from './web/core/interceptors/loader.interceptor';
import { ErrorService } from './web/shared/services/error.service';
import { NotificationService } from './web/shared/services/notification.service';
import { SeoService } from './web/shared/services/seo.service';

// States //
import { AccountState } from './web/shared/store/state/account.state';
import { AttributeState } from './web/shared/store/state/attribute.state';
import { AuthState } from './web/shared/store/state/auth.state';
import { BlogState } from './web/shared/store/state/blog.state';
import { BrandState } from './web/shared/store/state/brand.state';
import { CartState } from './web/shared/store/state/cart.state';
import { CategoryState } from './web/shared/store/state/category.state';
import { CompareState } from './web/shared/store/state/compare.state';
import { CountryState } from './web/shared/store/state/country.state';
import { CouponState } from './web/shared/store/state/coupon.state';
import { CurrencyState } from './web/shared/store/state/currency.state';
import { DownloadState } from './web/shared/store/state/download.state';
import { LoaderState } from './web/shared/store/state/loader.state';
import { MenuState } from './web/shared/store/state/menu.state';
import { NotificationState } from './web/shared/store/state/notification.state';
import { OrderStatusState } from './web/shared/store/state/order-status.state';
import { OrderState } from './web/shared/store/state/order.state';
import { PageState } from './web/shared/store/state/page.state';
import { PaymentDetailsState } from './web/shared/store/state/payment-details.state';
import { PointState } from './web/shared/store/state/point.state';
import { ProductState } from './web/shared/store/state/product.state';
import { QuestionAnswersState } from './web/shared/store/state/questions-answers.state';
import { RefundState } from './web/shared/store/state/refund.state';
import { ReviewState } from './web/shared/store/state/review.state';
import { SettingState } from './web/shared/store/state/setting.state';
import { StateState } from './web/shared/store/state/state.state';
import { StoreState } from './web/shared/store/state/store.state';
import { SubscriptionState } from './web/shared/store/state/subscription.state';
import { TagState } from './web/shared/store/state/tag.state';
import { ThemeOptionState } from './web/shared/store/state/theme-option.state';
import { ThemeState } from './web/shared/store/state/theme.state';
import { WalletState } from './web/shared/store/state/wallet.state';
import { WishlistState } from './web/shared/store/state/wishlist.state';
import { ServerInterceptor } from './web/core/interceptors/server.interceptor';
import { CommonState } from './web/shared/store/state/common.state';
import { ProductCategoryState } from './web/shared/store/state/product-category.state';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

export const appConfig: ApplicationConfig = {
  providers: [
    CurrencyPipe,
    ErrorService,
    SeoService,
    NotificationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GlobalErrorHandlerInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerInterceptor,
      multi: true,
    },
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      CarouselModule,
      LoadingBarRouterModule,
      ToastrModule.forRoot({
        positionClass: 'toast-top-right',
        preventDuplicates: true
      }),
      TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'en'
    }),

      NgxsModule.forRoot([
        AccountState,
        AttributeState,
        BlogState,
        BrandState,
        CartState,
        CategoryState,
        CompareState,
        CountryState,
        CouponState,
        CurrencyState,
        DownloadState,
        LoaderState,
        MenuState,
        NotificationState,
        OrderState,
        OrderStatusState,
        PageState,
        PaymentDetailsState,
        PointState,
        ProductState,
        QuestionAnswersState,
        RefundState,
        ReviewState,
        SettingState,
        StateState,
        StoreState,
        SubscriptionState,
        TagState,
        ThemeOptionState,
        ThemeState,
        WalletState,
        WishlistState,
        CommonState,
        ProductCategoryState
      ]),
      NgxsModule.forFeature([AuthState]),
      RouterModule.forRoot(routes , {
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      NgxsStoragePluginModule.forRoot({
        key: [
          "auth",
          'account',
          "theme_option",
          "theme",
          "setting",
          // "cart"
        ]
      })
    ),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideClientHydration()
  ]
};
